import {
  QueryClient,
  UseSuspenseQueryOptions,
  infiniteQueryOptions,
  queryOptions,
  skipToken,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { travelApprovalKey } from '@/module/Travel/Approval/model/TravelApprovalQuery';
import { travelRegistryKey } from '@/module/Travel/Registry/model/TravelRegistryQuery';

import {
  Id,
  PagedResponse,
  getQueryKey,
  invalidateAfterDelete,
  rethrowDecodedBlobError,
  saveBlobResponse,
} from '@feature/utils';

import * as api from './TravelRequestApi';
import {
  TRAVEL_DAY_BALANCE_PEOPLE_PAGE_SIZE,
  TravelActivityType,
  TravelCancelRequest,
  TravelDayBalancePeopleExportRequest,
  TravelDayBalancePeopleFilter,
  TravelDayBalancePreviewParams,
  TravelDocumentKinds,
  TravelFileDownload,
  TravelFileUploadRequest,
  TravelOverlap,
  TravelOverlapRequest,
  TravelOwnDayBalance,
  TravelPaperTitleCheck,
  TravelPaperTitleCheckParams,
  TravelRequest,
  TravelRequestParams,
  TravelRequestRequest,
  TravelRequestSummary,
} from './TravelRequestTypes';

export const travelRequestKey = getQueryKey('travelRequest');

const PAPER_TITLE_CHECK_KEY = 'paper-title-check';

export function getTravelRequestPagedOptions(
  params: TravelRequestParams,
): UseSuspenseQueryOptions<PagedResponse<TravelRequestSummary>> {
  return {
    queryKey: travelRequestKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getTravelRequestOptions(travelRequestId: Id): UseSuspenseQueryOptions<TravelRequest> {
  return {
    queryKey: travelRequestKey.one(travelRequestId),
    queryFn: () => api.get(travelRequestId),
  };
}

export function getTravelRequestOverlapsOptions(travelRequestId: Id): UseSuspenseQueryOptions<TravelOverlap[]> {
  return {
    queryKey: travelRequestKey.as([travelRequestId, 'overlaps']),
    queryFn: () => api.getOverlaps(travelRequestId),
  };
}

export function getTravelPaperTitleCheckOptions(
  params: TravelPaperTitleCheckParams,
): UseSuspenseQueryOptions<TravelPaperTitleCheck> {
  return {
    queryKey: travelRequestKey.as([PAPER_TITLE_CHECK_KEY, params]),
    queryFn: () => api.checkPaperTitle(params),
  };
}

export function getTravelUnfinishedErasmusOptions(): UseSuspenseQueryOptions<TravelOverlapRequest[]> {
  return {
    queryKey: travelRequestKey.as(['unfinished-erasmus']),
    queryFn: () => api.getUnfinishedErasmus(),
  };
}

export function getTravelUnfinishedReturnsOptions(): UseSuspenseQueryOptions<TravelOverlapRequest[]> {
  return {
    queryKey: travelRequestKey.as(['unfinished-returns']),
    queryFn: () => api.getUnfinishedReturns(),
  };
}

export function getTravelDocumentKindsOptions(
  activityType: TravelActivityType,
  withMobilitySupport = false,
): UseSuspenseQueryOptions<TravelDocumentKinds> {
  return {
    queryKey: travelRequestKey.as(['document-kinds', activityType, withMobilitySupport]),
    queryFn: () => api.getDocumentKinds(activityType, withMobilitySupport),
  };
}

export function useTravelRequestCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: TravelRequestRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: travelRequestKey.root(),
      }),
  });
}

export function useTravelRequestEdit(travelRequestId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: TravelRequestRequest) => api.edit(travelRequestId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: travelRequestKey.root(),
      }),
  });
}

function invalidateTravelRequests(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: travelRequestKey.root() });
}

// A board decision file moves the request between the segments of the office's board list.
function invalidateTravelFileViews(queryClient: QueryClient) {
  return Promise.all([
    invalidateTravelRequests(queryClient),
    queryClient.invalidateQueries({ queryKey: travelApprovalKey.root() }),
  ]);
}

function invalidateTravelStatusViews(queryClient: QueryClient) {
  return Promise.all([
    invalidateTravelRequests(queryClient),
    queryClient.invalidateQueries({ queryKey: travelApprovalKey.root() }),
    queryClient.invalidateQueries({ queryKey: travelRegistryKey.root() }),
  ]);
}

// A refused action means the cached request is stale; unawaited, so the caller reports the error before the refetch unmounts it.
function refreshRefused(invalidate: () => Promise<unknown>) {
  void invalidate();
}

export function useTravelRequestDelete(travelRequestId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(travelRequestId),
    onSuccess: () => invalidateAfterDelete(queryClient, travelRequestKey, travelRequestId),
    onError: () => refreshRefused(() => invalidateTravelRequests(queryClient)),
  });
}

export function useTravelRequestSubmit(travelRequestId: Id) {
  const queryClient = useQueryClient();
  // Waiting on the title check too would outlast the submit step, which unmounts before its success callback runs.
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: travelRequestKey.root(),
      predicate: query => query.queryKey[1] !== PAPER_TITLE_CHECK_KEY,
    });
  return useMutation({
    mutationFn: () => api.submit(travelRequestId),
    onSuccess: invalidate,
    onError: () => refreshRefused(invalidate),
  });
}

export function useTravelRequestCancel(travelRequestId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: TravelCancelRequest) => api.cancel(travelRequestId, request),
    onSuccess: () => invalidateTravelStatusViews(queryClient),
    onError: () => refreshRefused(() => invalidateTravelStatusViews(queryClient)),
  });
}

// Plain query options: the form keeps working while the preview loads or fails, since the server check stands.
export function getTravelDayBalancePreviewOptions(params: TravelDayBalancePreviewParams | null) {
  return queryOptions({
    queryKey: travelRequestKey.as(['day-balance-preview', params]),
    queryFn: params == null ? skipToken : () => api.getDayBalancePreview(params),
  });
}

export function getTravelOwnDayBalanceOptions(
  year: number,
  userId?: Id | null,
): UseSuspenseQueryOptions<TravelOwnDayBalance> {
  return {
    queryKey: travelRequestKey.as(['day-balance', year, userId ?? null]),
    queryFn: () => api.getOwnDayBalance(year, userId),
  };
}

export function getTravelDayBalancePeopleOptions(filter: TravelDayBalancePeopleFilter, search: string | null) {
  return infiniteQueryOptions({
    queryKey: travelRequestKey.as(['day-balance-people', 'paged', filter, search]),
    queryFn: ({ pageParam }) =>
      api.getDayBalancePeople({
        ...filter,
        search,
        page: pageParam,
        pageSize: TRAVEL_DAY_BALANCE_PEOPLE_PAGE_SIZE,
        sortDirection: 'ASC',
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) =>
      pages.reduce((loaded, page) => loaded + page.list.length, 0) < lastPage.count ? pages.length : undefined,
  });
}

export function useTravelDayBalancePeopleExport() {
  return useMutation({
    mutationFn: (params: TravelDayBalancePeopleExportRequest) =>
      api
        .exportDayBalancePeople(params)
        .then(response =>
          saveBlobResponse(
            response,
            params.lang === 'en' ? `Travel_days_${params.year}.xlsx` : `Zile_deplasare_${params.year}.xlsx`,
          ),
        )
        .catch(rethrowDecodedBlobError),
  });
}

export function useTravelRequestComplete(travelRequestId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.complete(travelRequestId),
    onSuccess: () => invalidateTravelRequests(queryClient),
    onError: () => refreshRefused(() => invalidateTravelRequests(queryClient)),
  });
}

export function useTravelRequestAddFile(travelRequestId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: TravelFileUploadRequest) => api.addFile(travelRequestId, request),
    onSuccess: () => invalidateTravelFileViews(queryClient),
    onError: () => refreshRefused(() => invalidateTravelRequests(queryClient)),
  });
}

export function useTravelRequestDeleteFile(travelRequestId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileId: Id) => api.deleteFile(travelRequestId, fileId),
    onSuccess: () => invalidateTravelFileViews(queryClient),
    onError: () => refreshRefused(() => invalidateTravelRequests(queryClient)),
  });
}

export function useTravelRequestDownloadFile(travelRequestId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (download: TravelFileDownload) =>
      api
        .downloadFile(travelRequestId, download.fileId)
        .then(response => saveBlobResponse(response, download.fallbackFilename))
        // A blob request gets its 403/404 error body as a Blob, which the notification would read as a MIME type.
        .catch(() => {
          throw new Error('file download failed');
        }),
    onError: () => refreshRefused(() => invalidateTravelRequests(queryClient)),
  });
}

export function useTravelRequestFileBlob(travelRequestId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileId: Id) =>
      api
        .downloadFile(travelRequestId, fileId)
        .then(response => response.data)
        .catch(rethrowDecodedBlobError),
    onError: () => refreshRefused(() => invalidateTravelRequests(queryClient)),
  });
}

export function useTravelRequestDocumentBlob(travelRequestId: Id) {
  return useMutation({
    mutationFn: () =>
      api
        .downloadDocument(travelRequestId)
        .then(response => response.data)
        .catch(rethrowDecodedBlobError),
  });
}

export function useTravelRequestDownloadDocument(travelRequestId: Id) {
  return useMutation({
    mutationFn: (fallbackFilename: string) =>
      api
        .downloadDocument(travelRequestId)
        .then(response => saveBlobResponse(response, fallbackFilename))
        .catch(rethrowDecodedBlobError),
  });
}

export function useTravelRequestDownloadTravelOrder(travelRequestId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fallbackFilename: string) =>
      api
        .downloadTravelOrder(travelRequestId)
        .then(response => saveBlobResponse(response, fallbackFilename))
        .catch(rethrowDecodedBlobError),
    // The requester's first download approves the request server-side, and a refused one means the cached status is stale.
    onSettled: () => invalidateTravelStatusViews(queryClient),
  });
}
